import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { Badge, Box, Stack, Typography, Tooltip } from '@mui/material';
import { useSectionActive } from './SectionActive';

// The contextual panel: the second half of the sidebar, whose contents
// belong to whichever section you are in.
//
// This is the answer to "two sidebars". The rail (68px, dark) is the app —
// it never changes. The panel (232px, paper) is the section — Files' vault
// rail, Operations' views and filters, Users' segments. They share one edge
// and read as one nav unit, so Files no longer stacks a 232px rail beside a
// 240px sidebar for 472px of chrome; it is 300px, on every screen, and
// nothing moves horizontally when you navigate.
//
// Content is delivered by portal rather than by lifting state into the
// shell: a page renders <NavPanel> wherever is convenient and it lands in
// the right column, with no prop drilling and no effect that has to keep a
// copy of a React tree in a useState.

const PanelSlotContext = createContext<HTMLElement | null>(null);

export const PanelSlotProvider: React.FC<{
  children: (setSlot: (el: HTMLDivElement | null) => void) => React.ReactNode;
}> = ({ children }) => {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  return (
    <PanelSlotContext.Provider value={el}>{children(setEl)}</PanelSlotContext.Provider>
  );
};

/** Renders its children into the sidebar's contextual panel. */
export const NavPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const el = useContext(PanelSlotContext);
  // A section kept alive in the background does not draw its panel.
  const active = useSectionActive();
  if (!el || !active) return null;
  return createPortal(<>{children}</>, el);
};

// ------------------------------------------------------------- primitives

export const PanelGroup: React.FC<{ label?: string; children: React.ReactNode }> = ({
  label, children,
}) => (
  <Box sx={{ mb: 2 }}>
    {label && (
      <Typography variant="overline" sx={{ px: 1.5, color: 'text.disabled', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
    )}
    <Stack gap={0.25}>{children}</Stack>
  </Box>
);

/**
 * One row in the panel.
 *
 * A `count` is context and stays quiet; a `badge` is a demand and is red.
 * Only "needs attention" style rows get the loud one, and only when it is
 * not zero — that rule came from the vault rail and is worth keeping.
 */
export const PanelLink: React.FC<{
  to: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  count?: number;
  badge?: number;
  alert?: boolean;
  /** A small red dot: something in here needs doing. Its words are the hover text. */
  dot?: string | null;
  /** Match this path exactly rather than by prefix. */
  exact?: boolean;
  currentPath: string;
  /**
   * Overrides URL matching, for a section whose pages live under other
   * URLs — a driver's file is /files/entity/driver/… but belongs to
   * "Drivers & staff" at /files/roster/driver.
   */
  active?: boolean;
}> = ({ to, label, icon, count, badge, alert, dot, exact, currentPath, active: activeOverride }) => {
  const active = activeOverride ?? (exact
    ? currentPath === to
    : currentPath === to || currentPath.startsWith(`${to}/`));

  return (
    <Box
      component={NavLink}
      to={to}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.5, py: 0.9, borderRadius: 2,
        textDecoration: 'none',
        color: active ? 'primary.dark' : 'text.secondary',
        bgcolor: active ? 'primary.light' : 'transparent',
        fontWeight: active ? 700 : 500,
        fontSize: 14,
        '&:hover': { bgcolor: active ? 'primary.light' : 'grey.100' },
        '& svg': { fontSize: 19 },
      }}
    >
      {icon}
      <Box
        component="span"
        sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label}
      </Box>
      {!!badge && badge > 0 && <Badge color="error" badgeContent={badge} sx={{ mr: 1.25 }} />}
      {dot && (
        <Tooltip title={dot} placement="right">
          <Box component="span" aria-label={dot} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
        </Tooltip>
      )}
      {count !== undefined && (
        <Typography
          component="span"
          variant="caption"
          sx={{ color: alert ? 'error.main' : 'text.disabled', fontWeight: alert ? 700 : 500 }}
        >
          {count}
        </Typography>
      )}
    </Box>
  );
};

/** Same shape as PanelLink but for in-page state rather than a route. */
export const PanelButton: React.FC<{
  label: React.ReactNode;
  icon?: React.ReactNode;
  count?: number;
  active?: boolean;
  onClick: () => void;
}> = ({ label, icon, count, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.25, width: '100%',
      px: 1.5, py: 0.9, borderRadius: 2, border: 0, cursor: 'pointer',
      font: 'inherit', textAlign: 'left',
      color: active ? 'primary.dark' : 'text.secondary',
      bgcolor: active ? 'primary.light' : 'transparent',
      fontWeight: active ? 700 : 500,
      fontSize: 14,
      '&:hover': { bgcolor: active ? 'primary.light' : 'grey.100' },
      '& svg': { fontSize: 19 },
    }}
  >
    {icon}
    <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Box>
    {count !== undefined && (
      <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>
        {count}
      </Typography>
    )}
  </Box>
);
